/**
 * Baby Development Content Page
 * Manage content blocks per category (4 tabs)
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Trash2, GripVertical, Save, Activity, Eye, MessageCircle, Heart, Baby } from 'lucide-react';
import toast from 'react-hot-toast';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { RichTextEditor } from '@/components/ui/RichTextEditor';
import { LocaleSelector } from '@/components/LocaleSelector';
import { useAdminLocale } from '@/hooks/useAdminLocale';
import type { BabyDevelopmentPhase, BabyDevelopmentCategory, BabyDevelopmentContent, ContentBlock } from './types';

const CATEGORY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  motorik: Activity,
  sinne: Eye,
  kommunikation: MessageCircle,
  sozial: Heart,
};

export function BabyDevelopmentContentPage() {
  const supabase = getSupabaseAdminClient();
  const { id: phaseId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { contentLocale } = useAdminLocale();

  const [activeCategory, setActiveCategory] = useState<string>('motorik');
  const [contentBlocks, setContentBlocks] = useState<ContentBlock[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch phase
  const { data: phase } = useQuery({
    queryKey: ['baby-development-phase', phaseId, contentLocale],
    queryFn: async () => {
      if (!phaseId) throw new Error('No phase ID');
      const { data, error } = await (supabase as any)
        .from('baby_development_phases')
        .select('*')
        .eq('id', phaseId)
        .eq('locale', contentLocale)
        .single();
      if (error) throw error;
      return data as BabyDevelopmentPhase;
    },
    enabled: !!phaseId,
  });

  // Fetch categories (locale-aware)
  const { data: categories } = useQuery({
    queryKey: ['baby-development-categories', contentLocale],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('baby_development_categories')
        .select('*')
        .eq('locale', contentLocale)
        .order('sort_order');
      if (error) throw error;
      return data as BabyDevelopmentCategory[];
    },
  });

  // Fetch content for active category
  const { data: content, isLoading } = useQuery({
    queryKey: ['baby-development-content', phaseId, activeCategory, contentLocale],
    queryFn: async () => {
      if (!phaseId) throw new Error('No phase ID');
      const category = categories?.find(c => c.slug === activeCategory);
      if (!category) return null;

      const { data, error } = await (supabase as any)
        .from('baby_development_content')
        .select('*')
        .eq('phase_id', phaseId)
        .eq('category_id', category.id)
        .eq('locale', contentLocale)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
      return data as BabyDevelopmentContent | null;
    },
    enabled: !!phaseId && !!categories && categories.length > 0,
  });

  // Update local state when content changes
  useEffect(() => {
    if (content) {
      setContentBlocks(content.content_blocks || []);
      setHasChanges(false);
    } else {
      setContentBlocks([]);
      setHasChanges(false);
    }
  }, [content]);

  // Reset blocks when category or content changes
  const handleCategoryChange = (slug: string) => {
    if (hasChanges) {
      if (!confirm('Ungespeicherte Änderungen gehen verloren. Fortfahren?')) {
        return;
      }
    }
    setActiveCategory(slug);
    setHasChanges(false);
  };

  // Save content
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!phaseId) throw new Error('No phase ID');
      const category = categories?.find(c => c.slug === activeCategory);
      if (!category) throw new Error('Category not found');

      if (content) {
        // Update existing
        const { error } = await (supabase as any)
          .from('baby_development_content')
          .update({ content_blocks: contentBlocks, locale: contentLocale })
          .eq('id', content.id);
        if (error) throw error;
      } else {
        // Insert new
        const { error } = await (supabase as any)
          .from('baby_development_content')
          .insert({
            phase_id: phaseId,
            category_id: category.id,
            content_blocks: contentBlocks,
            locale: contentLocale,
          });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success('Content gespeichert');
      setHasChanges(false);
      queryClient.invalidateQueries({ queryKey: ['baby-development-content', phaseId, activeCategory, contentLocale] });
    },
    onError: (error) => {
      toast.error('Fehler: ' + (error as Error).message);
    },
  });

  // Block management
  const addBlock = (type: ContentBlock['type']) => {
    let newBlock: ContentBlock;
    switch (type) {
      case 'text':
        newBlock = { type: 'text', content: '' };
        break;
      case 'list':
        newBlock = { type: 'list', title: '', items: [''] };
        break;
      case 'tip':
        newBlock = { type: 'tip', title: '', content: '' };
        break;
    }
    setContentBlocks([...contentBlocks, newBlock]);
    setHasChanges(true);
  };

  const updateBlock = (index: number, updates: Partial<ContentBlock>) => {
    const newBlocks = [...contentBlocks];
    newBlocks[index] = { ...newBlocks[index], ...updates } as ContentBlock;
    setContentBlocks(newBlocks);
    setHasChanges(true);
  };

  const removeBlock = (index: number) => {
    setContentBlocks(contentBlocks.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const moveBlock = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= contentBlocks.length) return;

    const newBlocks = [...contentBlocks];
    [newBlocks[index], newBlocks[newIndex]] = [newBlocks[newIndex], newBlocks[index]];
    setContentBlocks(newBlocks);
    setHasChanges(true);
  };

  // Early return after all hooks
  if (!phaseId) {
    return <div className="text-center py-12 text-slate-500">Phase nicht gefunden</div>;
  }

  if (isLoading && !categories) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/features/baby-development')}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex items-center gap-4 flex-1">
          <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center">
            <Baby className="w-6 h-6 text-teal-600" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900">Entwicklungs-Content: {phase?.display_range ?? ''}</h1>
            <p className="text-slate-500">Phase {phase?.phase_number}: "{phase?.subtitle}"</p>
          </div>
          <LocaleSelector />
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
        {categories?.map((category) => {
          const Icon = CATEGORY_ICONS[category.slug] || Activity;
          const isActive = activeCategory === category.slug;
          return (
            <button
              key={category.id}
              onClick={() => handleCategoryChange(category.slug)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                isActive
                  ? 'bg-white shadow-sm text-teal-600'
                  : 'text-slate-600 hover:bg-slate-200'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span className="font-medium">{category.title}</span>
            </button>
          );
        })}
      </div>

      {/* Content Editor */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        {/* Add Block Buttons */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => addBlock('text')}
            className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
          >
            <Plus className="w-4 h-4" />
            Text
          </button>
          <button
            onClick={() => addBlock('list')}
            className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
          >
            <Plus className="w-4 h-4" />
            Liste
          </button>
          <button
            onClick={() => addBlock('tip')}
            className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
          >
            <Plus className="w-4 h-4" />
            Tipp
          </button>
        </div>

        {/* Content Blocks */}
        <div className="space-y-4">
          {contentBlocks.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              Noch kein Content vorhanden. Füge Blöcke hinzu.
            </div>
          )}

          {contentBlocks.map((block, index) => (
            <div key={index} className="border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="flex flex-col">
                  <button
                    onClick={() => moveBlock(index, 'up')}
                    disabled={index === 0}
                    className="p-1 hover:bg-slate-100 rounded disabled:opacity-30"
                  >
                    <GripVertical className="w-4 h-4 text-slate-400 rotate-90" />
                  </button>
                  <button
                    onClick={() => moveBlock(index, 'down')}
                    disabled={index === contentBlocks.length - 1}
                    className="p-1 hover:bg-slate-100 rounded disabled:opacity-30"
                  >
                    <GripVertical className="w-4 h-4 text-slate-400 -rotate-90" />
                  </button>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded ${
                  block.type === 'text' ? 'bg-blue-100 text-blue-700' :
                  block.type === 'list' ? 'bg-green-100 text-green-700' :
                  'bg-amber-100 text-amber-700'
                }`}>
                  {block.type === 'text' ? 'Text' : block.type === 'list' ? 'Liste' : 'Tipp'}
                </span>
                <div className="flex-1" />
                <button
                  onClick={() => removeBlock(index)}
                  className="p-2 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>

              {/* Block Content Editor */}
              {block.type === 'text' && (
                <RichTextEditor
                  value={block.content}
                  onChange={(html) => updateBlock(index, { content: html })}
                  placeholder="Text eingeben..."
                  minHeight="120px"
                />
              )}

              {block.type === 'list' && (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={block.title || ''}
                    onChange={(e) => updateBlock(index, { title: e.target.value })}
                    placeholder="Listen-Titel (optional)"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                  {block.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex gap-2">
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => {
                          const newItems = [...block.items];
                          newItems[itemIndex] = e.target.value;
                          updateBlock(index, { items: newItems });
                        }}
                        placeholder={`Punkt ${itemIndex + 1}`}
                        className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                      <button
                        onClick={() => {
                          const newItems = block.items.filter((_, i) => i !== itemIndex);
                          updateBlock(index, { items: newItems.length ? newItems : [''] });
                        }}
                        className="p-2 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => updateBlock(index, { items: [...block.items, ''] })}
                    className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700"
                  >
                    <Plus className="w-4 h-4" />
                    Punkt hinzufügen
                  </button>
                </div>
              )}

              {block.type === 'tip' && (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={block.title}
                    onChange={(e) => updateBlock(index, { title: e.target.value })}
                    placeholder="Tipp-Titel"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                  <RichTextEditor
                    value={block.content}
                    onChange={(html) => updateBlock(index, { content: html })}
                    placeholder="Tipp-Inhalt..."
                    minHeight="100px"
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Save Button */}
        <div className="flex justify-end mt-6 pt-6 border-t">
          <button
            onClick={() => saveMutation.mutate()}
            disabled={!hasChanges || saveMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            {saveMutation.isPending ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
